import type { Moment } from 'moment';
import moment from 'moment';

export const PERIOD = {
  HOUR: 'HOUR',
  DAY: 'DAY',
  MONTH: 'MONTH',
  YEAR: 'YEAR',
} as const;

export const isValidDatetime = (datetime?: string | Moment) =>
  datetime && moment(datetime, 'YYYY-MM-DDTHH:mm:ss.sssZ', true).isValid();

export const isSameOrAfterToday = (date: string) => {
  const today = moment(new Date()).startOf('day');

  return moment(date).isSameOrAfter(today);
};

export const isSameOrBeforeToday = (date: string) => {
  const today = moment(new Date()).startOf('day');

  return moment(date).isSameOrBefore(today);
};

export const dateFormatsArray = (
  array = [],
  D = 'D',
  M = 'M',
  invert = true,
): string[] => {
  let separator = '/';

  for (let i = 0; i < 2; i++) {
    for (let d = 1; d <= 2; d++) {
      for (let m = 1; m <= 2; m++) {
        for (let y = 2; y <= 4; y += 2) {
          array.push(
            `${D.repeat(d)}${separator}${M.repeat(m)}${separator}${'Y'.repeat(
              y,
            )}`,
          );
        }
      }
    }

    separator = '-';
  }

  if (invert) {
    return dateFormatsArray(array, 'M', 'D', false);
  }

  return array;
};

const formatDateValid = dateFormatsArray();

export const isValidAnyDate = (date: string) =>
  moment(date, formatDateValid, true).isValid();

export const formatAnyDateToMongoDate = (date: string | null) => {
  if (isValidAnyDate(date)) {
    return moment(date, formatDateValid).utc().toISOString();
  }

  return null;
};

export function dateRange(
  startDate: string | Date | null,
  endDate: string | Date | null,
  interval: keyof typeof PERIOD,
): Moment[] {
  // if date wasn't passed use epoch time and current date
  const start = startDate
    ? moment(startDate)
    : moment(new Date(0).toISOString());

  const end = endDate ? moment(endDate) : moment();

  switch (interval) {
    case PERIOD.HOUR:
      return [...Array(end.diff(start, 'hours') + 1).keys()].map((i) =>
        start.clone().add(i, 'hours'),
      );

    case PERIOD.DAY:
      return [...Array(end.diff(start, 'days') + 1).keys()].map((i) =>
        start.clone().add(i, 'days'),
      );

    case PERIOD.MONTH:
      return [...Array(end.diff(start, 'months') + 1).keys()].map((i) =>
        start.clone().add(i, 'months'),
      );

    case PERIOD.YEAR:
      return [...Array(end.diff(start, 'years') + 1).keys()].map((i) =>
        start.clone().add(i, 'years'),
      );

    default:
      throw new Error('Invalid interval supplied to date range.');
  }
}

export const isSameDay = (a: Moment, b: Moment, fmt?: string) =>
  moment(a).utc().format(fmt) === moment(b).utc().format(fmt);

export const formatToHumanDate = (date: string | null) => {
  const dateMs = moment(date).valueOf();

  return moment(dateMs).format('DD/MM/YYYY');
};

export const periodToDay = (value: number, period: string) =>
  ({
    [PERIOD.HOUR]: value / 24,
    [PERIOD.DAY]: value,
    [PERIOD.MONTH]: value * 30,
    [PERIOD.YEAR]: value * 365,
  })[period];
